import React, { useCallback, useEffect, useState } from 'react';
import {
  Users, Layers, Activity, Search, X, ShieldCheck, Save, UserPlus,
  KeyRound, Trash2, Mail, ArrowRightLeft,
} from 'lucide-react';
import BotaoFechar, { useEscFecha } from '../components/BotaoFechar';
import api from '../lib/api';
import { formatDateBR } from '../lib/format';
import { toast } from 'sonner';

/*
 * Esta tela descrevia um SaaS que não existe: planos Free/Pro/Premium, MRR,
 * preços de R$ 29,90 e R$ 49,90 fixos no código. Era herança do modelo da
 * Emergent — nenhum usuário sequer tinha o campo `plan` no banco.
 *
 * Removido na Etapa D2f. No lugar entraram os números que são reais e o que
 * o administrador de fato precisa fazer: convidar gente e destravar senhas.
 */

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [convites, setConvites] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [gerando, setGerando] = useState(null);
  const [linkGerado, setLinkGerado] = useState(null);
  const [convidando, setConvidando] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, m, c] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/metrics'),
        api.get('/admin/convites'),
      ]);
      setUsers(u.data); setMetrics(m.data); setConvites(c.data || []);
    } catch (err) {
      if (err?.response?.status === 403) toast.error('Acesso restrito', { description: 'Apenas administradores.' });
      else toast.error('Erro ao carregar', { description: err?.message });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  /** Link para o usuário criar uma senha nova. Funciona sem servidor de e-mail. */
  const gerarLinkSenha = async (u) => {
    setGerando(u.user_id);
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/link-recuperacao`);
      setLinkGerado({ ...data, titulo: 'Link de redefinição de senha', validade: `${data.expira_em_minutos} minutos` });
    } catch (err) {
      toast.error('Não foi possível gerar o link', { description: err?.response?.data?.detail || err.message });
    } finally { setGerando(null); }
  };

  const revogarConvite = async (email) => {
    try {
      await api.delete(`/admin/convites/${encodeURIComponent(email)}`);
      toast.success('Convite revogado');
      load();
    } catch (err) {
      toast.error('Erro ao revogar', { description: err?.response?.data?.detail || err.message });
    }
  };

  const filtered = users.filter((u) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q);
  });

  if (loading) return <div className="text-slate-500 dark:text-slate-400">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-500 flex items-center justify-center shadow-md">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100">Admin</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Usuários, convites e números do sistema</p>
          </div>
        </div>
        <button onClick={() => setConvidando(true)} data-testid="admin-convidar"
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg shadow-md">
          <UserPlus className="w-4 h-4" /> Convidar usuário
        </button>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Metric icon={Users} label="Usuários" value={metrics.total_users} color="indigo" />
          <Metric icon={ShieldCheck} label="Administradores" value={metrics.total_admins} color="amber" />
          <Metric icon={Layers} label="Projetos" value={metrics.total_projects} color="blue" />
          <Metric icon={ArrowRightLeft} label="Lançamentos" value={metrics.total_transactions} color="emerald" />
          <Metric icon={Activity} label="Sessões ativas" value={metrics.sessoes_ativas} color="indigo" />
        </div>
      )}

      {convites.length > 0 && (
        <div className="card p-5" data-testid="admin-convites">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
            <Mail className="w-4 h-4 text-indigo-600" /> Convites pendentes ({convites.length})
          </h3>
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {convites.map((c) => (
              <div key={c.email} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-slate-800 dark:text-slate-100 truncate">{c.email}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {c.role === 'admin' ? 'Administrador' : 'Usuário'}
                    {c.expirado && <span className="ml-2 text-rose-600 dark:text-rose-400 font-medium">expirado</span>}
                  </div>
                </div>
                <button onClick={() => revogarConvite(c.email)}
                  className="text-xs px-2 py-1 rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> Revogar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por e-mail ou nome..." className="input-md pl-10 pr-10" data-testid="admin-search" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          )}
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
              <tr className="text-left text-slate-600 dark:text-slate-400">
                <th className="px-4 py-3">Usuário</th>
                <th className="px-4 py-3">Papel</th>
                <th className="px-4 py-3 text-right">Projetos</th>
                <th className="px-4 py-3 text-right">Lançamentos</th>
                <th className="px-4 py-3 text-right">Sessões</th>
                <th className="px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {filtered.map((u) => (
                <tr key={u.user_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 dark:text-slate-100 truncate">{u.name || '-'}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{u.email}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {u.role === 'admin'
                      ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">Administrador</span>
                      : <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">Usuário</span>}
                    {u.totp_enabled && <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">2FA</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{u.projects_count ?? 0}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{u.transactions_count ?? 0}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{u.sessoes_ativas ?? 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      <button onClick={() => setEditing(u)} data-testid={`admin-edit-${u.user_id}`}
                        className="text-xs px-2 py-1 rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50">
                        Editar
                      </button>
                      <button onClick={() => gerarLinkSenha(u)} data-testid={`admin-link-senha-${u.user_id}`}
                        disabled={gerando === u.user_id}
                        title="Gera um endereço para este usuário criar uma senha nova"
                        className="text-xs px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 flex items-center gap-1">
                        <KeyRound className="w-3 h-3" />
                        {gerando === u.user_id ? 'Gerando...' : 'Link de senha'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="6" className="p-8 text-center text-slate-500 dark:text-slate-400">Nenhum usuário encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <EditModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {convidando && (
        <ConviteModal onClose={() => setConvidando(false)}
          onCriado={(dados) => {
            setConvidando(false);
            setLinkGerado({ ...dados, titulo: 'Convite criado', validade: `${dados.expira_em_dias} dias` });
            load();
          }} />
      )}
      {linkGerado && <LinkModal dados={linkGerado} onClose={() => setLinkGerado(null)} />}
    </div>
  );
}

function Metric({ icon: Icon, label, value, color }) {
  const c = { indigo: 'from-indigo-500 to-blue-500', emerald: 'from-emerald-500 to-teal-500', amber: 'from-amber-500 to-orange-500', blue: 'from-blue-500 to-cyan-500' }[color];
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg bg-gradient-to-br ${c} shadow`}><Icon className="w-4 h-4 text-white" /></div>
      </div>
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

/** Mostra o link uma vez — ele não é recuperável depois, só se gera outro. */
function LinkModal({ dados, onClose }) {
  const [copiado, setCopiado] = useState(false);
  useEscFecha(onClose);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(dados.link);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      toast.error('Não foi possível copiar', { description: 'Selecione o texto e copie manualmente.' });
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-link-modal">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{dados.titulo}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">para {dados.email}</p>
          </div>
          <BotaoFechar onClose={onClose} />
        </div>

        <div className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 p-3 text-xs break-all font-mono text-slate-700 dark:text-slate-300">
          {dados.link}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <button onClick={copiar} data-testid="admin-link-copiar"
            className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-500 text-white font-medium">
            {copiado ? 'Copiado!' : 'Copiar link'}
          </button>
          <button onClick={onClose}
            className="px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700">
            Fechar
          </button>
        </div>

        <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg px-3 py-2.5 mt-4">
          Vale por <strong>{dados.validade}</strong> e só pode ser usado uma vez.
          Entregue por um canal privado — quem tiver este endereço define a senha desta conta.
        </p>
      </div>
    </div>
  );
}

function ConviteModal({ onClose, onCriado }) {
  useEscFecha(onClose);
  const [form, setForm] = useState({ email: '', name: '', role: 'user' });
  const [enviando, setEnviando] = useState(false);

  const criar = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      const { data } = await api.post('/admin/convites', form);
      onCriado(data);
    } catch (err) {
      toast.error('Não foi possível convidar', { description: err?.response?.data?.detail || err.message });
    } finally { setEnviando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="admin-convite-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Convidar usuário</h3>
          <BotaoFechar onClose={onClose} testid="admin-convite-fechar" />
        </div>
        <form onSubmit={criar} className="p-6 space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Você gera o convite; a pessoa define a própria senha. Ninguém precisa digitá-la por ela.
          </p>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">E-mail</label>
            <input type="email" required value={form.email} data-testid="admin-convite-email"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="pessoa@exemplo.com" className="input-md mt-1" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Nome (opcional)</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-md mt-1" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Papel</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input-md mt-1" data-testid="admin-convite-papel">
              <option value="user">Usuário</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">Cancelar</button>
            <button type="submit" disabled={enviando} data-testid="admin-convite-criar"
              className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg font-medium">
              {enviando ? 'Criando...' : 'Gerar convite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditModal({ user, onClose, onSaved }) {
  useEscFecha(onClose);
  const [form, setForm] = useState({ name: user.name || '', role: user.role || 'user' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.user_id}`, form);
      toast.success('Usuário atualizado');
      onSaved();
    } catch (err) {
      toast.error('Erro ao salvar', { description: err?.response?.data?.detail || err.message });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Editar Usuário</h3>
          <BotaoFechar onClose={onClose} testid="admin-edit-fechar" />
        </div>
        <div className="p-6 space-y-4">
          <div className="text-sm">
            <div className="text-slate-500 dark:text-slate-400">{user.email}</div>
            {user.created_at && (
              <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                no sistema desde {formatDateBR(user.created_at)}
              </div>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Nome</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input-md mt-1" data-testid="edit-name" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Papel</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input-md mt-1" data-testid="edit-role">
              <option value="user">Usuário</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="edit-save"
            className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg font-medium flex items-center justify-center gap-2">
            <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
